import { useEffect, useRef } from "react";

type WidgetId = string | number;

interface TurnstileApi {
  render(container: HTMLElement, options: {
    sitekey: string;
    action: string;
    callback: (token: string) => void;
    "expired-callback": () => void;
    "error-callback": () => void;
    "timeout-callback": () => void;
  }): WidgetId;
  reset(widgetId: WidgetId): void;
  remove(widgetId: WidgetId): void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

export function TurnstileWidget({
  siteKey,
  action,
  resetSignal,
  onToken,
  onError,
}: {
  siteKey: string;
  action: string;
  resetSignal: number;
  onToken: (token: string) => void;
  onError: (message: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<WidgetId | null>(null);

  useEffect(() => {
    if (!siteKey || !container.current) return;
    let active = true;
    const render = () => {
      if (!active || !window.turnstile || !container.current || widget.current !== null) return;
      widget.current = window.turnstile.render(container.current, {
        sitekey: siteKey,
        action,
        callback: onToken,
        "expired-callback": () => onToken(""),
        "error-callback": () => { onToken(""); onError("The security check could not load. Refresh and try again."); },
        "timeout-callback": () => { onToken(""); onError("The security check timed out. Try again."); },
      });
    };
    if (window.turnstile) render();
    else {
      let script = document.querySelector<HTMLScriptElement>("script[data-scopevora-turnstile]");
      if (!script) {
        script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.scopevoraTurnstile = "true";
        document.head.append(script);
      }
      script.addEventListener("load", render, { once: true });
      script.addEventListener("error", () => onError("The security check could not load. Refresh and try again."), { once: true });
    }
    return () => {
      active = false;
      if (widget.current !== null && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [action, onError, onToken, siteKey]);

  useEffect(() => {
    if (resetSignal > 0 && widget.current !== null && window.turnstile) {
      window.turnstile.reset(widget.current);
      onToken("");
    }
  }, [onToken, resetSignal]);

  return <div className="turnstile-widget" ref={container} aria-label="Security check" />;
}
